import { c as _c } from "react/compiler-runtime";
import { IcoBolt16, IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import type { ApiNodeDataAction, DecisionStatus, GraphOperationUpdateNode, NodeConfigurationContextAction, VaultKind } from '@onefootprint/request-types/dashboard';
import type { TemplateAutocompleteItem } from '@onefootprint/ui';
import { Button, Divider, Form, Hint, IconButton, LinkButton, SegmentedControl, Select } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '../../hooks/use-dirty';
import { useGetNodeConfigContext } from '../../hooks/use-get-node-config-context';
import { useGetStatusesWithDeactivated } from '../../hooks/use-get-statuses';
import useStore from '../../hooks/use-store';
import type { ActionFormData, ActionType, FormManualReviewKind, TagSource } from '../../playbook-builder.types';
import createActionFormPayload from '../../utils/create-action-form-paylod';
import type { ApplyEditMutation } from '../base-node';
type ActionFormProps = {
  data: ApiNodeDataAction;
  nodeId: string;
  subject: VaultKind;
  mutation: ApplyEditMutation;
};
const actionTypeOrder: ActionType[] = ['setDecision', 'tag', 'note', 'manualReview', 'setEntityStatus'];

/** This looks a little funny. This is a typescript typesafe enumeration of all `DecisionStatus`es.
 * We can use this to generate the list of all `DecisionStatus`es, and typescript will enforce that we keep this list in sync with the type itself
 * See: https://stackoverflow.com/questions/45626602/coverting-typescript-string-literal-types-to-an-array-of-string and
 * https://onefootprint.slack.com/archives/C07FV5WPQSG/p1749069031111459
 * */
const mapOfDecisionStatuses: Record<DecisionStatus, DecisionStatus> = {
  none: 'none',
  pass: 'pass',
  fail: 'fail'
};
const DECISION_STATUS_OPTIONS: DecisionStatus[] = Object.values(mapOfDecisionStatuses);
type BaseInputProps = {
  fieldName: keyof ActionFormData;
  canEdit: boolean;
  placeholder?: string;
};
const SetDecisionInput = t0 => {
  const $ = _c(15);
  const {
    fieldName,
    canEdit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "action.options"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[1] !== canEdit || $[2] !== fieldName || $[3] !== t) {
    const decisionTranslations = {
      none: t(_temp),
      pass: t(_temp2),
      fail: t(_temp3)
    };
    T0 = Form.Select;
    t2 = fieldName;
    t3 = "compact";
    t4 = !canEdit;
    t5 = DECISION_STATUS_OPTIONS.map(status => ({
      label: decisionTranslations[status],
      value: status
    }));
    $[1] = canEdit;
    $[2] = fieldName;
    $[3] = t;
    $[4] = T0;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    T0 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== T0 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <T0 name={t2} size={t3} disabled={t4} options={t5} />;
    $[9] = T0;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
const getAutocompleteItems = (config?: NodeConfigurationContextAction): Record<string, TemplateAutocompleteItem[]> => Object.entries(config?.autocomplete ?? {}).reduce<Record<string, TemplateAutocompleteItem[]>>((acc, [key, value]) => {
  acc[key] = value.map(item => ({
    label: item.display,
    value: item.token,
    detail: item.dataType.join(' | ')
  }));
  return acc;
}, {} as Record<string, TemplateAutocompleteItem[]>);

/** Tokens that can be used as an expression tag: those that resolve to a string or list of strings. */
const getExpressionTokenOptions = (config?: NodeConfigurationContextAction) => (config?.autocomplete.tokens ?? []).filter(token => token.dataType.some(type => type === 'string' || type === 'array' || type === 'unknown')).map(token => ({
  label: token.display,
  // Stored/evaluated as a `{{ token }}` template reference.
  value: `{{ ${token.token} }}`
}));
const TagsInput = t0 => {
  const $ = _c(32);
  const {
    canEdit,
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "action.hints.tag"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "action.options.errors"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tErrors
  } = useTranslation("playbook-builder", t2);
  const {
    control,
    setValue
  } = useFormContext();
  let t3;
  if ($[2] !== control) {
    t3 = {
      control,
      name: "addTags"
    };
    $[2] = control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t3);
  const tagValues = useWatch({
    control,
    name: "addTags"
  }) ?? [];
  let t4;
  if ($[4] !== config) {
    t4 = getExpressionTokenOptions(config);
    $[4] = config;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const expressionOptions = t4;
  let t5;
  if ($[6] !== setValue) {
    t5 = (index, source) => {
      setValue(`addTags.${index}.source`, source, {
        shouldDirty: true
      });
      setValue(`addTags.${index}.value`, "", {
        shouldDirty: true
      });
    };
    $[6] = setValue;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleSourceChange = t5;
  let t6;
  if ($[8] !== canEdit || $[9] !== expressionOptions || $[10] !== fields || $[11] !== handleSourceChange || $[12] !== remove || $[13] !== t || $[14] !== tErrors || $[15] !== tagValues) {
    let t7;
    if ($[17] !== canEdit || $[18] !== expressionOptions || $[19] !== handleSourceChange || $[20] !== remove || $[21] !== t || $[22] !== tErrors || $[23] !== tagValues) {
      t7 = (field, index_0) => {
        const source_0 = tagValues[index_0]?.source ?? "manual";
        return <div key={field.id} className="grid grid-cols-[auto_1fr_auto] gap-3 items-start"><SegmentedControl size="compact" aria-label={t(_temp4)} options={[{
            label: t(_temp5),
            value: "manual"
          }, {
            label: t(_temp6),
            value: "expression"
          }]} value={source_0} onChange={value => handleSourceChange(index_0, value)} />{source_0 === "expression" ? <Form.Select name={`addTags.${index_0}.value`} size="compact" disabled={!canEdit} placeholder={t(_temp7)} options={expressionOptions} rules={{
            required: tErrors(_temp8)
          }} /> : <Form.Input name={`addTags.${index_0}.value`} size="compact" disabled={!canEdit} placeholder={t(_temp9)} rules={{
            validate: value_0 => value_0?.toString().trim() ? true : tErrors(_temp10)
          }} />}{canEdit && <IconButton icon={IcoTrash16} size="compact" variant="ghost" destructive={true} onClick={() => remove(index_0)} aria-label={t(_temp11)} />}</div>;
      };
      $[17] = canEdit;
      $[18] = expressionOptions;
      $[19] = handleSourceChange;
      $[20] = remove;
      $[21] = t;
      $[22] = tErrors;
      $[23] = tagValues;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    t6 = fields.map(t7);
    $[8] = canEdit;
    $[9] = expressionOptions;
    $[10] = fields;
    $[11] = handleSourceChange;
    $[12] = remove;
    $[13] = t;
    $[14] = tErrors;
    $[15] = tagValues;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[25] !== append || $[26] !== canEdit || $[27] !== t) {
    t7 = canEdit && <Button variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={() => append({
      source: "manual",
      value: ""
    })} fullWidth={false}>{t(_temp12)}</Button>;
    $[25] = append;
    $[26] = canEdit;
    $[27] = t;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t6 || $[30] !== t7) {
    t8 = <div className="flex flex-col gap-3"><div className="flex flex-col gap-3 p-3 border border-tertiary rounded-md">{t6}{t7}</div></div>;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};
const NoteInput = t0 => {
  const $ = _c(13);
  const {
    fieldName,
    canEdit,
    placeholder,
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "action"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] !== config) {
    t2 = getAutocompleteItems(config);
    $[1] = config;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const autocompleteItems = t2;
  const t3 = placeholder || "";
  const t4 = !canEdit;
  let t5;
  let t6;
  if ($[3] !== t) {
    t5 = {
      validate: value => {
        if (!value || value.toString().trim() === "") {
          return t(_temp13);
        }
        return true;
      }
    };
    t6 = t(_temp14);
    $[3] = t;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] !== autocompleteItems || $[7] !== fieldName || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = <Form.TemplateTextarea name={fieldName} placeholder={t3} disabled={t4} autocompleteItems={autocompleteItems} rules={t5} hint={t6} />;
    $[6] = autocompleteItems;
    $[7] = fieldName;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
const ManualReviewInput = t0 => {
  const $ = _c(12);
  const {
    fieldName,
    canEdit,
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "action.options"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] !== t) {
    t2 = kind => {
      if (kind === "rule_triggered") {
        return t(_temp15);
      }
      if (kind === "document_needs_review") {
        return t(_temp16);
      }
      return kind;
    };
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const getManualReviewTranslation = t2;
  let t3;
  if ($[3] !== config || $[4] !== getManualReviewTranslation) {
    t3 = () => {
      if (!config?.availableReviewTypes?.length) {
        return [{
          label: getManualReviewTranslation("rule_triggered"),
          value: "kind:rule_triggered"
        }, {
          label: getManualReviewTranslation("document_needs_review"),
          value: "kind:document_needs_review"
        }];
      }
      return config.availableReviewTypes.map(type => {
        if (type.optionKind === "legacy") {
          return {
            label: getManualReviewTranslation(type.kind),
            value: `kind:${type.kind}`
          };
        }
        return {
          label: type.name,
          value: `reviewKindId:${type.reviewKindId}`
        };
      });
    };
    $[3] = config;
    $[4] = getManualReviewTranslation;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getOptions = t3;
  let t4;
  if ($[6] !== getOptions) {
    t4 = getOptions();
    $[6] = getOptions;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !canEdit;
  let t6;
  if ($[8] !== fieldName || $[9] !== t4 || $[10] !== t5) {
    t6 = <Form.Select name={fieldName} options={t4} disabled={t5} size="compact" />;
    $[8] = fieldName;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
const SetEntityStatusInput = t0 => {
  const $ = _c(33);
  const {
    fieldName,
    canEdit,
    subject
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "action.hints.setStatus"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const name = fieldName as 'setEntityStatuses';
  const {
    control
  } = useFormContext();
  let t2;
  if ($[1] !== control || $[2] !== name) {
    t2 = {
      control,
      name
    };
    $[1] = control;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t2);
  const statusValues = useWatch({
    control,
    name
  }) ?? [];
  const currentStatusIds = statusValues.map(_temp17).filter(Boolean);
  const statuses = useGetStatusesWithDeactivated(currentStatusIds);
  const usedStatusIds = statusValues.map(_temp18).filter(Boolean);
  let t3;
  if ($[4] !== subject) {
    t3 = s => !s.isDeactivated && s.entityType === subject;
    $[4] = subject;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const assignableStatuses = statuses.filter(t3);
  const hasUnassignedStatuses = assignableStatuses.some(s_0 => !usedStatusIds.includes(s_0.id));
  const getAvailableStatuses = currentStatusId => statuses.filter(s_1 => assignableStatuses.includes(s_1) && !usedStatusIds.includes(s_1.id) || s_1.id === currentStatusId);
  let t4;
  if ($[6] !== statuses) {
    t4 = statusId => {
      const status = statuses.find(s_2 => s_2.id === statusId);
      return status?.entries ?? [];
    };
    $[6] = statuses;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const getEntryOptions = t4;
  let t5;
  if ($[8] !== append) {
    t5 = () => {
      append({
        statusId: "",
        entryId: ""
      });
    };
    $[8] = append;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleAdd = t5;
  const t6 = "flex flex-col gap-3";
  const t7 = "flex flex-col gap-3 p-3 border border-tertiary rounded-md";
  let t8;
  if ($[10] !== t) {
    t8 = t(_temp19);
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <span className="text-body-2 text-tertiary">{t8}</span>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t) {
    t10 = t(_temp20);
    $[14] = t;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10) {
    t11 = <span className="text-body-2 text-tertiary">{t10}</span>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="w-8" />;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t11 || $[20] !== t9) {
    t13 = <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">{t9}{t11}{t12}</div>;
    $[19] = t11;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Divider />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = fields.map((field, index) => {
    const statusId_0 = statusValues[index]?.statusId ?? "";
    const selectedStatus = statuses.find(s_3 => s_3.id === statusId_0);
    const entries = getEntryOptions(statusId_0);
    const availableStatuses = getAvailableStatuses(statusId_0);
    return <div key={field.id} className="flex flex-col"><div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-start"><Form.Select name={`${name}.${index}.statusId`} size="compact" disabled={!canEdit || availableStatuses.length === 0 || selectedStatus?.isDeactivated} options={availableStatuses.map(_temp21)} rules={{
          required: t(_temp22)
        }} /><Form.Select name={`${name}.${index}.entryId`} size="compact" disabled={!canEdit || !statusId_0 || entries.length === 0 || selectedStatus?.isDeactivated} options={entries.map(_temp23)} rules={{
          required: t(_temp24)
        }} />{canEdit && <IconButton icon={IcoTrash16} size="compact" variant="ghost" destructive={true} onClick={() => remove(index)} aria-label={t(_temp25)} />}</div>{selectedStatus?.isDeactivated && <Hint>{t(_temp26)}</Hint>}</div>;
  });
  let t16;
  if ($[23] !== canEdit || $[24] !== handleAdd || $[25] !== hasUnassignedStatuses || $[26] !== t) {
    t16 = canEdit && hasUnassignedStatuses && <Button variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={handleAdd} fullWidth={false}>{t(_temp27)}</Button>;
    $[23] = canEdit;
    $[24] = handleAdd;
    $[25] = hasUnassignedStatuses;
    $[26] = t;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t13 || $[29] !== t14 || $[30] !== t15 || $[31] !== t16) {
    t17 = <div className={t6}><div className={t7}>{t13}{t14}{t15}{t16}</div></div>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
const getFieldName = (type: ActionType): keyof ActionFormData | null => {
  if (type === 'setDecision') {
    return 'setDecision';
  }
  if (type === 'tag') {
    return 'addTags';
  }
  if (type === 'note') {
    return 'addAnnotations';
  }
  if (type === 'manualReview') {
    return 'raiseManualReview';
  }
  if (type === 'setEntityStatus') {
    return 'setEntityStatuses';
  }
  return null;
};
const ActionForm = ({
  nodeId,
  data,
  subject,
  mutation
}: ActionFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'action.options'
  });
  const config = useGetNodeConfigContext(nodeId, 'action');
  const {
    data: {
      canEdit
    }
  } = useStore();
  const onSubmit = async (formData: ActionFormData) => {
    const updatedNodeData = createActionFormPayload(formData);
    const operation: GraphOperationUpdateNode = {
      kind: 'update_node',
      nodeId: nodeId,
      nodeData: updatedNodeData
    };
    try {
      await mutation.mutateAsync(operation);
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
  const initial: ActionType[] = [];
  if (data.setDecision) {
    initial.push('setDecision');
  }
  if (data.raiseManualReview) {
    initial.push('manualReview');
  }
  if (data.addTags && data.addTags.length > 0) {
    initial.push('tag');
  }
  if (data.addAnnotations && data.addAnnotations.length > 0) {
    initial.push('note');
  }
  if (data.setEntityStatuses && data.setEntityStatuses.length > 0) {
    initial.push('setEntityStatus');
  }
  const getDefaultManualReviewKind = (): FormManualReviewKind => {
    if (!data.raiseManualReview) {
      return `kind:rule_triggered`;
    }
    if ('kind' in data.raiseManualReview) {
      return `kind:${data.raiseManualReview.kind}`;
    }
    return `reviewKindId:${data.raiseManualReview.reviewKindId}`;
  };
  const form = useForm<ActionFormData>({
    defaultValues: {
      visibleActions: [...new Set(initial)],
      setDecision: data.setDecision || DECISION_STATUS_OPTIONS[0],
      addTags: data.addTags?.map(tag => ({
        source: tag.kind,
        value: tag.value
      })) || [],
      addAnnotations: data.addAnnotations?.map(a => a.note).join(';') || '',
      raiseManualReview: getDefaultManualReviewKind(),
      setEntityStatuses: data.setEntityStatuses || []
    }
  });
  useEffect(() => {
    if (data.raiseManualReview || !config?.availableReviewTypes?.length) return;
    if (form.getFieldState('raiseManualReview').isDirty) return;
    const defaultReviewType = config.availableReviewTypes[0];
    const value: FormManualReviewKind = defaultReviewType.optionKind === 'legacy' ? `kind:${defaultReviewType.kind}` : `reviewKindId:${defaultReviewType.reviewKindId}`;
    form.setValue('raiseManualReview', value);
  }, [config, data.raiseManualReview, form]);
  const {
    handleSubmit,
    setValue,
    formState,
    watch
  } = form;
  const visibleActions = watch('visibleActions');
  const {
    toggle
  } = useDirty();
  useEffect(() => {
    toggle('action-form', formState.isDirty);
  }, [formState.isDirty]);
  const handleActionTypeChange = (index: number, newType: ActionType) => {
    const oldType = visibleActions[index];
    if (oldType === newType) {
      return;
    }
    const oldFieldName = getFieldName(oldType);
    if (oldFieldName) {
      const emptyValue = oldType === 'setEntityStatus' || oldType === 'tag' ? [] : '';
      setValue(oldFieldName, emptyValue, {
        shouldDirty: true
      });
    }
    if (newType === 'setEntityStatus') {
      setValue('setEntityStatuses', [{
        entryId: '',
        statusId: ''
      }], {
        shouldDirty: true
      });
    }
    if (newType === 'tag') {
      setValue('addTags', [{
        source: 'manual',
        value: ''
      }], {
        shouldDirty: true
      });
    }
    setValue('visibleActions', visibleActions.map((type, i) => i === index ? newType : type), {
      shouldDirty: true
    });
  };
  const addAction = () => {
    const nextAction = actionTypeOrder.find(type_0 => !visibleActions.includes(type_0));
    if (nextAction) {
      if (nextAction === 'tag') {
        setValue('addTags', [{
          source: 'manual',
          value: ''
        }], {
          shouldDirty: true
        });
      }
      setValue('visibleActions', [...visibleActions, nextAction], {
        shouldDirty: true
      });
    }
  };
  const deleteAction = (indexToDelete: number) => {
    const typeToDelete = visibleActions[indexToDelete];
    const fieldNameToClear = getFieldName(typeToDelete);
    if (fieldNameToClear) {
      if (typeToDelete === 'setEntityStatus' || typeToDelete === 'tag') {
        setValue(fieldNameToClear, [], {
          shouldDirty: true
        });
      } else {
        setValue(fieldNameToClear, '', {
          shouldDirty: true
        });
      }
    }
    setValue('visibleActions', visibleActions.filter((_, i_0) => i_0 !== indexToDelete), {
      shouldDirty: true
    });
  };
  const canAddMoreActions = visibleActions.length < actionTypeOrder.length;
  return <FormProvider {...form}>
      <form id="action-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col">
            {visibleActions.map((actionType, index_0) => <div key={actionType}>
                <Action index={index_0} type={actionType} visibleActions={visibleActions} onActionTypeChange={handleActionTypeChange} onDelete={deleteAction} canEdit={canEdit} config={config} subject={subject} />
                {index_0 < visibleActions.length - 1 && <div className="h-3 w-[1px] bg-gray-100 ml-6" />}
              </div>)}
          </div>
          {canEdit && canAddMoreActions && <LinkButton iconComponent={IcoPlusSmall16} iconPosition="left" onClick={addAction} disabled={!canAddMoreActions}>
              {t($ => $.addAction)}
            </LinkButton>}
        </div>
      </form>
    </FormProvider>;
};
type ActionProps = {
  index: number;
  type: ActionType;
  visibleActions: ActionType[];
  onActionTypeChange: (index: number, newType: ActionType) => void;
  onDelete: (index: number) => void;
  canEdit: boolean;
  config?: NodeConfigurationContextAction;
  subject: VaultKind;
};
const Action = t0 => {
  const $ = _c(114);
  const {
    index,
    type,
    visibleActions,
    onActionTypeChange,
    onDelete,
    canEdit,
    config,
    subject
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "action"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "operations"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tOperations
  } = useTranslation("playbook-builder", t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = _temp28;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let T0;
  let fieldName;
  let label;
  let placeholder;
  let t10;
  let t11;
  let t12;
  let t13;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== canEdit || $[4] !== index || $[5] !== onActionTypeChange || $[6] !== onDelete || $[7] !== t || $[8] !== tOperations || $[9] !== type || $[10] !== visibleActions) {
    const actionLabels = {
      setDecision: t(t3),
      tag: t(_temp29),
      note: t(_temp30),
      manualReview: t(_temp31),
      setEntityStatus: t(_temp32)
    };
    let t14;
    if ($[25] !== type || $[26] !== visibleActions) {
      t14 = optionType => optionType === type || !visibleActions.includes(optionType);
      $[25] = type;
      $[26] = visibleActions;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    const availableOptions = actionTypeOrder.filter(t14);
    let t15;
    if ($[28] !== type) {
      t15 = getFieldName(type);
      $[28] = type;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    fieldName = t15;
    let t16;
    if ($[30] !== index || $[31] !== onActionTypeChange) {
      t16 = value => {
        onActionTypeChange(index, value as ActionType);
      };
      $[30] = index;
      $[31] = onActionTypeChange;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    const handleSelectChange = t16;
    let t17;
    if ($[33] !== t) {
      t17 = currentType => {
        if (currentType === "setDecision") {
          return t(_temp33);
        }
        if (currentType === "manualReview") {
          return t(_temp34);
        }
        if (currentType === "tag") {
          return t(_temp35);
        }
        if (currentType === "note") {
          return t(_temp36);
        }
        if (currentType === "setEntityStatus") {
          return null;
        }
        return t(_temp37);
      };
      $[33] = t;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    const getActionLabel = t17;
    let t18;
    if ($[35] !== t) {
      t18 = currentType_0 => {
        const hints = {
          setDecision: t(_temp38),
          tag: t(_temp39),
          note: t(_temp40),
          setEntityStatus: t(_temp41)
        };
        return hints[currentType_0];
      };
      $[35] = t;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    const getActionHint = t18;
    let t19;
    if ($[37] !== t) {
      t19 = currentType_1 => {
        const placeholders = {
          note: t(_temp42, {
            defaultValue: "Add notes here..."
          })
        };
        return placeholders[currentType_1];
      };
      $[37] = t;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    const getPlaceholder = t19;
    let t20;
    if ($[39] !== getActionHint || $[40] !== type) {
      t20 = getActionHint(type);
      $[39] = getActionHint;
      $[40] = type;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    const actionHint = t20;
    let t21;
    if ($[42] !== getPlaceholder || $[43] !== type) {
      t21 = getPlaceholder(type);
      $[42] = getPlaceholder;
      $[43] = type;
      $[44] = t21;
    } else {
      t21 = $[44];
    }
    placeholder = t21;
    let t22;
    if ($[45] !== getActionLabel || $[46] !== type) {
      t22 = getActionLabel(type);
      $[45] = getActionLabel;
      $[46] = type;
      $[47] = t22;
    } else {
      t22 = $[47];
    }
    label = t22;
    t12 = "border rounded border-tertiary";
    let t23;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <IcoBolt16 />;
      $[48] = t23;
    } else {
      t23 = $[48];
    }
    const t24 = index + 1;
    let t25;
    if ($[49] !== t || $[50] !== t24) {
      t25 = t(_temp43, {
        index: t24
      });
      $[49] = t;
      $[50] = t24;
      $[51] = t25;
    } else {
      t25 = $[51];
    }
    let t26;
    if ($[52] !== t25) {
      t26 = <div className="flex items-center gap-1">{t23}<span className="text-label-2 text-primary">{t25}</span></div>;
      $[52] = t25;
      $[53] = t26;
    } else {
      t26 = $[53];
    }
    let t27;
    if ($[54] !== canEdit || $[55] !== index || $[56] !== onDelete || $[57] !== tOperations) {
      t27 = canEdit && <IconButton onClick={() => onDelete(index)} aria-label={tOperations(_temp44)} icon={IcoTrash16} size="tiny" destructive={true} />;
      $[54] = canEdit;
      $[55] = index;
      $[56] = onDelete;
      $[57] = tOperations;
      $[58] = t27;
    } else {
      t27 = $[58];
    }
    if ($[59] !== t26 || $[60] !== t27) {
      t13 = <header className="flex items-center justify-between gap-1 bg-secondary border-b border-tertiary h-[36px] px-4 py- rounded-t-default">{t26}{t27}</header>;
      $[59] = t26;
      $[60] = t27;
      $[61] = t13;
    } else {
      t13 = $[61];
    }
    t11 = "flex flex-col gap-6 p-4";
    T0 = Select;
    if ($[62] !== t) {
      t4 = t(_temp45);
      $[62] = t;
      $[63] = t4;
    } else {
      t4 = $[63];
    }
    t5 = "compact";
    t6 = type;
    t7 = actionHint;
    t8 = !canEdit;
    t9 = handleSelectChange;
    t10 = availableOptions.map(optionType_0 => ({
      label: actionLabels[optionType_0],
      value: optionType_0
    }));
    $[3] = canEdit;
    $[4] = index;
    $[5] = onActionTypeChange;
    $[6] = onDelete;
    $[7] = t;
    $[8] = tOperations;
    $[9] = type;
    $[10] = visibleActions;
    $[11] = T0;
    $[12] = fieldName;
    $[13] = label;
    $[14] = placeholder;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[11];
    fieldName = $[12];
    label = $[13];
    placeholder = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t14;
  if ($[64] !== T0 || $[65] !== t10 || $[66] !== t4 || $[67] !== t5 || $[68] !== t6 || $[69] !== t7 || $[70] !== t8 || $[71] !== t9) {
    t14 = <T0 label={t4} size={t5} value={t6} hint={t7} disabled={t8} onChange={t9} options={t10} />;
    $[64] = T0;
    $[65] = t10;
    $[66] = t4;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t8;
    $[71] = t9;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] !== label) {
    t15 = label && <div className="text-label-2 text-primary">{label}</div>;
    $[73] = label;
    $[74] = t15;
  } else {
    t15 = $[74];
  }
  let t16;
  if ($[75] !== canEdit || $[76] !== fieldName || $[77] !== type) {
    t16 = fieldName && type === "setDecision" && <SetDecisionInput fieldName={fieldName} canEdit={canEdit} />;
    $[75] = canEdit;
    $[76] = fieldName;
    $[77] = type;
    $[78] = t16;
  } else {
    t16 = $[78];
  }
  let t17;
  if ($[79] !== canEdit || $[80] !== config || $[81] !== type) {
    t17 = type === "tag" && <TagsInput canEdit={canEdit} config={config} />;
    $[79] = canEdit;
    $[80] = config;
    $[81] = type;
    $[82] = t17;
  } else {
    t17 = $[82];
  }
  let t18;
  if ($[83] !== canEdit || $[84] !== config || $[85] !== fieldName || $[86] !== placeholder || $[87] !== type) {
    t18 = fieldName && type === "note" && <NoteInput fieldName={fieldName} canEdit={canEdit} placeholder={placeholder} config={config} />;
    $[83] = canEdit;
    $[84] = config;
    $[85] = fieldName;
    $[86] = placeholder;
    $[87] = type;
    $[88] = t18;
  } else {
    t18 = $[88];
  }
  let t19;
  if ($[89] !== canEdit || $[90] !== config || $[91] !== fieldName || $[92] !== type) {
    t19 = fieldName && type === "manualReview" && <ManualReviewInput fieldName={fieldName} canEdit={canEdit} config={config} />;
    $[89] = canEdit;
    $[90] = config;
    $[91] = fieldName;
    $[92] = type;
    $[93] = t19;
  } else {
    t19 = $[93];
  }
  let t20;
  if ($[94] !== canEdit || $[95] !== fieldName || $[96] !== subject || $[97] !== type) {
    t20 = fieldName && type === "setEntityStatus" && <SetEntityStatusInput fieldName={fieldName} canEdit={canEdit} subject={subject} />;
    $[94] = canEdit;
    $[95] = fieldName;
    $[96] = subject;
    $[97] = type;
    $[98] = t20;
  } else {
    t20 = $[98];
  }
  let t21;
  if ($[99] !== t15 || $[100] !== t16 || $[101] !== t17 || $[102] !== t18 || $[103] !== t19 || $[104] !== t20) {
    t21 = <div className="flex flex-col gap-2">{t15}{t16}{t17}{t18}{t19}{t20}</div>;
    $[99] = t15;
    $[100] = t16;
    $[101] = t17;
    $[102] = t18;
    $[103] = t19;
    $[104] = t20;
    $[105] = t21;
  } else {
    t21 = $[105];
  }
  let t22;
  if ($[106] !== t11 || $[107] !== t14 || $[108] !== t21) {
    t22 = <div className={t11}>{t14}{t21}</div>;
    $[106] = t11;
    $[107] = t14;
    $[108] = t21;
    $[109] = t22;
  } else {
    t22 = $[109];
  }
  let t23;
  if ($[110] !== t12 || $[111] !== t13 || $[112] !== t22) {
    t23 = <section className={t12}>{t13}{t22}</section>;
    $[110] = t12;
    $[111] = t13;
    $[112] = t22;
    $[113] = t23;
  } else {
    t23 = $[113];
  }
  return t23;
};
export default ActionForm;
function _temp($) {
  return $.none;
}
function _temp2($_0) {
  return $_0.pass;
}
function _temp3($_1) {
  return $_1.fail;
}
function _temp4($) {
  return $.sourceAriaLabel;
}
function _temp5($_0) {
  return $_0.fixed;
}
function _temp6($_1) {
  return $_1.dynamic;
}
function _temp7($_2) {
  return $_2.expressionPlaceholder;
}
function _temp8($_3) {
  return $_3.tagsRequired;
}
function _temp9($_4) {
  return $_4.manualPlaceholder;
}
function _temp10($_5) {
  return $_5.tagsRequired;
}
function _temp11($_6) {
  return $_6.removeTag;
}
function _temp12($_7) {
  return $_7.addTag;
}
function _temp13($) {
  return $.options.errors.noteRequired;
}
function _temp14($_0) {
  return $_0.hints.note.templateHint;
}
function _temp15($) {
  return $.manualReviewReasons.rule_triggered;
}
function _temp16($_0) {
  return $_0.manualReviewReasons.document_needs_review;
}
function _temp17(v) {
  return v.statusId;
}
function _temp18(v_0) {
  return v_0.statusId;
}
function _temp19($) {
  return $.statusCategory;
}
function _temp20($_0) {
  return $_0.status;
}
function _temp21(status_0) {
  return {
    label: status_0.displayName,
    value: status_0.id
  };
}
function _temp22($_1) {
  return $_1.statusRequired;
}
function _temp23(entry) {
  return {
    label: entry.displayName,
    value: entry.id
  };
}
function _temp24($_2) {
  return $_2.entryRequired;
}
function _temp25($_3) {
  return $_3.removeStatus;
}
function _temp26($_4) {
  return $_4.statusDeactivated;
}
function _temp27($_5) {
  return $_5.addStatus;
}
function _temp28($) {
  return $.actionLabels.setDecision;
}
function _temp29($_0) {
  return $_0.actionLabels.tag;
}
function _temp30($_1) {
  return $_1.actionLabels.note;
}
function _temp31($_2) {
  return $_2.actionLabels.manualReview;
}
function _temp32($_3) {
  return $_3.actionLabels.setStatus;
}
function _temp33($_4) {
  return $_4.formLabels.decision;
}
function _temp34($_5) {
  return $_5.formLabels.manualReviewKind;
}
function _temp35($_6) {
  return $_6.formLabels.tag;
}
function _temp36($_7) {
  return $_7.formLabels.note;
}
function _temp37($_8) {
  return $_8.formLabels.value;
}
function _temp38($_9) {
  return $_9.hints.setDecision.action;
}
function _temp39($_10) {
  return $_10.hints.tag.action;
}
function _temp40($_11) {
  return $_11.hints.note.action;
}
function _temp41($_12) {
  return $_12.hints.setStatus.action;
}
function _temp42($_13) {
  return $_13.hints.note.templateHint;
}
function _temp43($_14) {
  return $_14.header;
}
function _temp44($_15) {
  return $_15.removeNode;
}
function _temp45($_16) {
  return $_16.formLabels.action;
}